import { Download, TriangleAlert } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { toast } from 'sonner'

import { JsonCodeBlock } from '@/components/JsonCodeBlock'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { triggerDownload } from '@/lib/download'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

import type { UnexpectedErrorReportRecord } from '../types'

interface UnexpectedErrorReportActionProps {
  reports: UnexpectedErrorReportRecord[]
  onOpen: () => void
}

export function UnexpectedErrorReportAction({
  reports,
  onOpen,
}: UnexpectedErrorReportActionProps) {
  if (reports.length === 0) return null

  const label =
    reports.length === 1
      ? 'Unexpected error report'
      : `${reports.length} unexpected error reports`

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onOpen}
      className="border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900">
      <TriangleAlert className="size-4" />
      {label}
    </Button>
  )
}

interface UnexpectedErrorReportsSheetProps {
  attemptId: string | null
  reports: UnexpectedErrorReportRecord[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function getAnalysis(report: Record<string, unknown>) {
  const value = report.analysis
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function SummaryItem({ label, value }: { label: string; value: unknown }) {
  const text = readString(value)
  if (!text) return null

  return (
    <div>
      <dt className="text-xs text-neutral-low-contrast">{label}</dt>
      <dd className="mt-1 text-sm whitespace-pre-wrap break-words text-primary-high-contrast">
        {text}
      </dd>
    </div>
  )
}

function formatCreatedAt(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function downloadReportJson(row: UnexpectedErrorReportRecord) {
  const json = JSON.stringify(row.report, null, 2)
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `unexpected-error-report-${row.id}.json`)
  setTimeout(() => URL.revokeObjectURL(url), 0)
  toast.success('Report JSON downloaded')
}

export function UnexpectedErrorReportsSheet({
  attemptId,
  reports,
  open,
  onOpenChange,
}: UnexpectedErrorReportsSheetProps) {
  const posthog = usePostHog()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-neutral-ui-bg overflow-y-auto gap-0 p-0 w-full max-w-none sm:max-w-lg rounded-l-xl">
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10 border-b border-gray-100">
          <SheetTitle className="flex items-center gap-2 text-left">
            <TriangleAlert className="size-5 text-amber-700" />
            Unexpected error {reports.length === 1 ? 'report' : 'reports'}
          </SheetTitle>
          <SheetDescription className="text-left">
            Attempt {attemptId ?? ''}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 px-4 py-4">
          {reports.length === 0 && (
            <p className="text-sm text-neutral-low-contrast">
              No unexpected error reports are currently available for this
              attempt.
            </p>
          )}
          {reports.map((row, index) => {
            const analysis = getAnalysis(row.report)
            return (
              <section
                key={row.id}
                className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-primary-high-contrast">
                    {reports.length === 1
                      ? 'Report'
                      : `Report ${index + 1} of ${reports.length}`}
                  </h3>
                  <span className="text-xs text-neutral-low-contrast">
                    {formatCreatedAt(row.created_at)}
                  </span>
                </div>

                <dl className="mt-4 space-y-3">
                  <SummaryItem
                    label="Message"
                    value={row.report.safe_message}
                  />
                  <SummaryItem
                    label="Error kind"
                    value={row.report.error_kind}
                  />
                  <SummaryItem label="Step" value={row.report.step_id} />
                  <SummaryItem label="Action" value={row.report.action} />
                  <SummaryItem
                    label="Failure phase"
                    value={row.report.failure_phase}
                  />
                  <SummaryItem label="Analysis" value={analysis?.summary} />
                  <SummaryItem
                    label="Likely reason"
                    value={analysis?.likely_reason}
                  />
                  <SummaryItem
                    label="Suggested condition name"
                    value={analysis?.suggested_name}
                  />
                  <SummaryItem
                    label="Suggested change location"
                    value={analysis?.suggested_change_location}
                  />
                </dl>

                <div className="mt-4 rounded-md border border-gray-200 bg-neutral-subtle-bg">
                  <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="text-sm font-medium text-primary-high-contrast">
                      Raw report JSON
                    </span>
                    <div className="flex items-center gap-2">
                      <CopyButton
                        content={JSON.stringify(row.report, null, 2)}
                        label="Copy report JSON"
                        onCopy={() => {
                          posthog?.capture('unexpected_error_report_copied', {
                            session_id: row.session_run_id,
                            attempt_id: row.attempt_id,
                            report_id: row.id,
                          })
                          toast.success('Report JSON copied to clipboard')
                        }}
                        onError={() =>
                          toast.error('Failed to copy report JSON')
                        }
                        className="text-neutral-low-contrast hover:text-primary-high-contrast"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          posthog?.capture(
                            'unexpected_error_report_downloaded',
                            {
                              session_id: row.session_run_id,
                              attempt_id: row.attempt_id,
                              report_id: row.id,
                            }
                          )
                          downloadReportJson(row)
                        }}
                        aria-label="Download report JSON"
                        title="Download report JSON"
                        className="h-auto w-auto p-0 text-neutral-low-contrast hover:bg-transparent hover:text-primary-high-contrast">
                        <Download className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 p-3">
                    <JsonCodeBlock
                      value={row.report}
                      label="report JSON"
                      showCopyButton={false}
                    />
                  </div>
                </div>
              </section>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
  )
}
